import { Sparkles, BarChart3, CheckSquare, TrendingUp, ArrowLeftRight } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useAuthStore } from "@/stores/auth-store"
import { useAiConversations } from "@/hooks/use-ai-conversations"
import { useAiStore } from "@/stores/ai-store"
import { useNavigate } from "react-router-dom"

const SUGGESTED_PROMPTS = [
  { icon: TrendingUp, label: "Podsumuj pipeline", desc: "Ile transakcji w każdej fazie i łączna wartość" },
  { icon: CheckSquare, label: "Moje zadania na dziś", desc: "Co mam do zrobienia dziś i jutro?" },
  { icon: BarChart3, label: "Statystyki CRM", desc: "Przychody, otwarte transakcje, aktywności" },
  { icon: ArrowLeftRight, label: "Porównanie sprzedaży", desc: "Porównaj ten i zeszły miesiąc" },
]

interface AiGreetingProps {
  onSend: (prompt: string) => void
}

export function AiGreeting({ onSend }: AiGreetingProps) {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const { data: conversationsData } = useAiConversations()
  const { setActiveConversation } = useAiStore()
  const navigate = useNavigate()

  const firstName = user?.first_name || "Użytkownik"
  const recentConversation = conversationsData?.data?.[0]

  return (
    <div className="flex flex-1 flex-col items-center justify-center px-6">
      <div className="w-full max-w-[520px] space-y-5">
        {/* Greeting */}
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--bg-surface-raised)]">
            <Sparkles size={18} className="text-[var(--text-muted)]" />
          </div>
          <h1 className="text-lg font-semibold tracking-tight text-foreground">
            {t("ai.greeting", { name: firstName, defaultValue: `Cześć, ${firstName}. Jak mogę pomóc?` })}
          </h1>
          <p className="mt-1 text-[13px] text-[var(--text-muted)]">
            Zapytaj o dane CRM, twórz rekordy, analizuj sprzedaż
          </p>
        </div>

        {/* Recent conversation chip */}
        {recentConversation && (
          <div className="flex justify-center">
            <button
              onClick={() => {
                setActiveConversation(recentConversation.id)
                navigate(`/ai/${recentConversation.id}`)
              }}
              className="rounded-full border px-3.5 py-1.5 text-[12px] text-[var(--text-muted)] transition-colors hover:border-[var(--border-strong)] hover:text-[var(--text-secondary)]"
            >
              Ostatnia: {recentConversation.title || "Nowa rozmowa"}
            </button>
          </div>
        )}

        {/* Suggested prompts — 2x2 grid */}
        <div className="grid grid-cols-2 gap-2">
          {SUGGESTED_PROMPTS.map((p) => {
            const Icon = p.icon
            return (
              <button
                key={p.label}
                onClick={() => onSend(p.label + " — " + p.desc)}
                className="group rounded-xl bg-[var(--bg-surface)] px-3.5 py-3 text-left shadow-[inset_0_0_0_1px_rgba(0,0,0,0),0_0_2px_rgba(28,40,64,0.18),0_1px_3px_rgba(24,41,75,0.04)] transition-all hover:shadow-[inset_0_0_0_1px_rgba(0,0,0,0),0_0_2px_rgba(28,40,64,0.24),0_2px_6px_rgba(24,41,75,0.06)]"
              >
                <div className="mb-1 flex items-center gap-2">
                  <Icon size={13} className="text-[var(--text-tertiary)] transition-colors group-hover:text-[var(--text-secondary)]" />
                  <span className="text-[13px] font-medium text-foreground">{p.label}</span>
                </div>
                <p className="pl-[21px] text-[11px] leading-snug text-[var(--text-muted)]">{p.desc}</p>
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}
